import { flexRender, type Cell, type RowData } from '@tanstack/react-table'
import { memo } from 'react'

import { RenderCount } from './RenderCount'
import { cn } from '@lib/cn'

/** Shared horizontal padding for the header row and body rows — keep in sync via this export. */
export const tablePaddingClass = 'px-1'

type TableCellProps<T extends RowData> = {
  cell: Cell<T, unknown>
  /** Hover-only column hidden in place while its dedicated overlay column is shown. */
  hideInPlace: boolean
  /** Dev-only: show the per-cell render-count badge. */
  showRenderCount: boolean
  /**
   * Forces memoized cells to re-render when table state used by cell renderers
   * changes while TanStack keeps the Cell object stable.
   */
  tableStateVersion?: unknown
}

function TableCellComponent<T extends RowData>({ cell, hideInPlace, showRenderCount }: TableCellProps<T>) {
  const textAlign = cell.column.columnDef.meta?.textAlign ?? 'left'

  return (
    <td
      className={cn(
        'relative px-1 py-1 flex items-center text-sm font-normal overflow-x-auto',
        hideInPlace && 'invisible',
      )}
      style={{ justifyContent: textAlign }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
      {showRenderCount && <RenderCount />}
    </td>
  )
}
const TableCell = memo(TableCellComponent) as typeof TableCellComponent

type TableRowProps<T extends RowData> = {
  rowId: string
  /**
   * Visible cells for this row. Passed as a prop (not derived from `row` inside)
   * because TanStack Table keeps `row` refs stable when only `columns` change
   * (e.g. async column data like the tags list loading in). `getVisibleCells()`
   * still gives the row component the current cell list after column changes.
   */
  cells: Array<Cell<T, unknown>>
  /** Vertical offset (px) for this virtual row. */
  start: number
  index: number
  gridTemplateColumns: string
  rowClassName: string
  isHoverColumnInView: boolean
  hasHoverColumn: boolean
  /** react-virtual `measureElement` ref for dynamic row height. */
  measureRef?: (node: Element | null) => void
  /** Dev-only: show per-row / per-cell render-count badges. */
  showRenderCount: boolean
  /**
   * Version/reference for table state that cell renderers may read indirectly
   * from `cell.getContext()` (row selection today). This keeps memoization safe.
   */
  tableStateVersion?: unknown
}

function TableRowComponent<T extends RowData>({
  rowId,
  cells,
  start,
  index,
  gridTemplateColumns,
  rowClassName,
  isHoverColumnInView,
  hasHoverColumn,
  measureRef,
  showRenderCount,
  tableStateVersion,
}: TableRowProps<T>) {
  return (
    <tr
      className={cn('absolute left-0 w-full group animate-fade-in transform-gpu', tablePaddingClass, rowClassName)}
      style={{ display: 'grid', gridTemplateColumns, transform: `translateY(${start}px)` }}
      data-index={index}
      ref={measureRef}
    >
      {showRenderCount && (
        // Wrapped in a <td> — a <tr> may only contain <td>/<th>. Absolute, so it takes no grid track.
        <td aria-hidden className='pointer-events-none absolute left-0 top-0 z-30 p-0'>
          <RenderCount variant='row' />
        </td>
      )}
      {cells.map((cell, cellIndex) => {
        const isHover = cell.column.columnDef.meta?.isHover

        return (
          <TableCell
            key={`${rowId}-${cellIndex}-cell`}
            cell={cell}
            hideInPlace={!!(isHover && !isHoverColumnInView)}
            showRenderCount={showRenderCount}
            tableStateVersion={tableStateVersion}
          />
        )
      })}

      {hasHoverColumn && !isHoverColumnInView && (
        <td
          className='absolute top-0 bottom-0 flex items-center pr-2 z-10 transition-opacity duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transform-gpu'
          style={{ left: 'var(--hover-anchor-right)', transform: 'translateX(-100%)' }}
        >
          {cells
            .filter((c) => c.column.columnDef.meta?.isHover)
            .map((c) => (
              <div key={c.id} className='shrink-0'>
                {flexRender(c.column.columnDef.cell, c.getContext())}
              </div>
            ))}
        </td>
      )}
    </tr>
  )
}
export const TableRow = memo(TableRowComponent) as typeof TableRowComponent
